import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

function PnlBadge({ pct }) {
  if (pct == null) return <span className="text-korinth-muted">—</span>;
  const color = pct > 0 ? 'text-korinth-accent' : pct < 0 ? 'text-korinth-danger' : 'text-korinth-muted';
  return <span className={`font-bold ${color}`}>{pct >= 0 ? '+' : ''}{pct.toFixed(2)}%</span>;
}

function DirIcon({ dir }) {
  if (dir === 'long') return <TrendingUp className="w-3 h-3 text-korinth-accent" />;
  if (dir === 'short') return <TrendingDown className="w-3 h-3 text-korinth-danger" />;
  return <Minus className="w-3 h-3 text-blue-400" />;
}

export default function ShadowTracker() {
  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [sRes, pRes, prRes] = await Promise.all([
          fetch('/api/shadow/stats'),
          fetch('/api/shadow/positions?status=open'),
          fetch('/api/shadow/predictions?status=open'),
        ]);
        if (sRes.ok) setStats(await sRes.json());
        if (pRes.ok) setPositions(await pRes.json());
        if (prRes.ok) setPredictions(await prRes.json());
      } catch { /* silent */ }
    }
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, []);

  const r = stats?.recommendations || {};
  const p = stats?.predictions || {};

  return (
    <ModuleContainer
      title={
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 w-full text-left">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <span>Shadow Tracker — Validation Scorecard</span>
          {r.open > 0 && <span className="text-[9px] font-mono text-korinth-accent ml-2">{r.open} open</span>}
        </button>
      }
      subtitle={expanded ? 'Each recommendation + prediction gets its own $100K virtual account' : null}
    >
      {expanded ? (
        <div className="space-y-6">
          {/* Recommendation Stats */}
          <div>
            <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Recommendations</h4>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {[
                { label: 'Open', value: r.open || 0, color: 'text-korinth-warning' },
                { label: 'Closed', value: r.closed || 0, color: 'text-korinth-text' },
                { label: 'Win Rate', value: r.winRate != null ? r.winRate + '%' : '—', color: (r.winRate || 0) >= 50 ? 'text-korinth-accent' : 'text-korinth-danger' },
                { label: 'Avg Return', value: r.avgReturnPct ? (r.avgReturnPct >= 0 ? '+' : '') + r.avgReturnPct.toFixed(2) + '%' : '—', color: (r.avgReturnPct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger' },
                { label: 'Total P&L', value: r.totalRealizedUsd ? '$' + r.totalRealizedUsd.toFixed(0) : '—', color: (r.totalRealizedUsd || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger' },
              ].map(s => (
                <div key={s.label} className="bg-korinth-bg rounded px-2 py-1.5 text-center">
                  <div className={`text-sm font-mono font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[8px] text-korinth-muted">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Open positions table */}
            {positions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] font-mono">
                  <thead><tr className="border-b border-korinth-border text-korinth-muted">
                    <th className="text-left py-1 px-2">Asset</th><th className="text-left py-1 px-2">Dir</th>
                    <th className="text-right py-1 px-2">Entry</th><th className="text-right py-1 px-2">Current</th>
                    <th className="text-right py-1 px-2">P&L</th><th className="text-right py-1 px-2">Max Fav</th>
                    <th className="text-right py-1 px-2">Conv</th>
                  </tr></thead>
                  <tbody>
                    {positions.map(pos => (
                      <tr key={pos.id} className="border-b border-korinth-border/30">
                        <td className="py-1 px-2 text-white font-bold">{pos.asset}</td>
                        <td className="py-1 px-2"><DirIcon dir={pos.direction} /></td>
                        <td className="py-1 px-2 text-right text-korinth-text">{pos.entry_price?.toFixed(2) || '—'}</td>
                        <td className="py-1 px-2 text-right text-korinth-text">{pos.current_price?.toFixed(2) || '—'}</td>
                        <td className="py-1 px-2 text-right"><PnlBadge pct={pos.unrealized_pnl_pct} /></td>
                        <td className="py-1 px-2 text-right"><PnlBadge pct={pos.max_favorable_pct} /></td>
                        <td className="py-1 px-2 text-right text-korinth-muted">{pos.conviction_at_entry?.toFixed(1) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Prediction Stats */}
          <div>
            <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Predictions</h4>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { label: 'Open', value: p.open || 0, color: 'text-korinth-warning' },
                { label: 'Resolved', value: p.closed || 0, color: 'text-korinth-text' },
                { label: 'Correct', value: p.correct || 0, color: 'text-korinth-accent' },
                { label: 'Hit Rate', value: p.hitRate != null ? p.hitRate + '%' : '—', color: (p.hitRate || 0) >= 50 ? 'text-korinth-accent' : 'text-korinth-danger' },
              ].map(s => (
                <div key={s.label} className="bg-korinth-bg rounded px-2 py-1.5 text-center">
                  <div className={`text-sm font-mono font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-[8px] text-korinth-muted">{s.label}</div>
                </div>
              ))}
            </div>

            {predictions.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {predictions.map(pred => (
                  <div key={pred.id} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                    <span className={pred.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>{pred.direction === 'bullish' ? '▲' : '▼'}</span>
                    <span className="text-white font-bold w-10">{pred.asset}</span>
                    <span className="text-korinth-muted px-1 py-0.5 rounded bg-korinth-border text-[8px]">{pred.timeframe}</span>
                    <span className="text-korinth-text">{pred.confidence}%</span>
                    <span className="ml-auto"><PnlBadge pct={pred.unrealized_pnl_pct} /></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4 text-[10px] font-mono text-korinth-muted">
          <span>Recs: {r.open || 0} open, {r.closed || 0} closed</span>
          <span>Preds: {p.open || 0} open, {p.closed || 0} resolved</span>
          <span className="text-korinth-muted/50">Click to expand</span>
        </div>
      )}
    </ModuleContainer>
  );
}
